import { HStack, Text } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

// import {
//   OrganizationGQLType,
//   TAccountTypeGQLType,
// } from 'types/dashboard-core-gqlTypes.generated';
import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { LedgerTransactionGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { CsvDownloadButton } from 'packages/common-frontend/components/CsvDownloadButton';
import { Select } from 'packages/common-frontend/components/Select';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';

interface ExpensesFeesTableHeaderProps {
  periods: { label: string; value: Util.FullDateStr }[];
  selectedPeriod: Util.FullDateStr;
  onChangeHandler: React.Dispatch<Util.FullDateStr>;
  isLoading: boolean;
  ledgerData: LedgerTransactionGQLType[];
}

const csvHeaders: {
  label: string;
  key: string;
}[] = [
  { label: 'Date', key: 'postingDate' },
  { label: 'Commitment Amount', key: 'commitmentAmount' },
  { label: 'Outstanding Balance', key: 'outstandingBalance' },
  { label: 'Backup Servicing Fee', key: 'accruedBackupServicingFee' },
  { label: 'Verification Fee', key: 'accruedVerificationAgentFee' },
  { label: 'Administrative Agent Fee', key: 'accruedAdminFee' },
  { label: 'Commitment Fee', key: 'accruedCommitmentFee' },
  { label: 'Unused Fee', key: 'accruedUnusedInterest' },
  { label: 'Other Accrued Fees and Expenses', key: 'accruedOtherFee' },
  { label: 'Interest Rate - SOFR Term 1 Month', key: 'interestRate' },
  { label: 'Interest Margin', key: 'interestMargin' },
  { label: 'Interest', key: 'accruedInterest' },
  { label: 'Servicing Fee', key: 'accruedServicingFee' },
];

// Set to start of period, determined by 1 business day prior
const sofrTermRates: { [effectiveDate: Util.FullDateStr]: string } = {
  '2023-02-01': '4.57248%',
  '2023-03-01': '4.66078%',
  '2023-04-01': '4.80247%',
  '2023-05-01': '5.01870%',
  '2023-06-01': '5.17227%',
  '2023-07-01': '5.14078%',
};

// Being real lazy here for demo lol
const outstandingBalanceData: {
  postingDate: Util.FullDateStr;
  balance: number;
}[] = _.reverse([
  { postingDate: '2023-07-25', balance: 83604111.71 },
  { postingDate: '2023-07-17', balance: 83474401.71 },
  { postingDate: '2023-07-13', balance: 80641166.71 },
  { postingDate: '2023-07-10', balance: 80571331.28 },
  { postingDate: '2023-07-03', balance: 83119974.25 },
  { postingDate: '2023-06-23', balance: 75597627.25 },
  { postingDate: '2023-06-13', balance: 74597627.25 },
  { postingDate: '2023-06-12', balance: 66033232.93 },
  { postingDate: '2023-06-08', balance: 65033232.93 },
  { postingDate: '2023-06-05', balance: 64033232.93 },
  { postingDate: '2023-06-02', balance: 20149378.23 },
  { postingDate: '2023-05-31', balance: 10531618.23 },
  { postingDate: '2023-05-31', balance: 10531618.23 },
  { postingDate: '2023-05-18', balance: 54907481.64 },
  { postingDate: '2023-05-12', balance: 54787909.64 },
  { postingDate: '2023-05-05', balance: 53253859.64 },
  { postingDate: '2023-04-18', balance: 73253859.64 },
  { postingDate: '2023-04-11', balance: 70624420.64 },
  { postingDate: '2023-04-05', balance: 71560119.36 },
  { postingDate: '2023-03-31', balance: 71513608.36 },
  { postingDate: '2023-03-21', balance: 70939661.36 },
  { postingDate: '2023-03-17', balance: 71228587.74 },
  { postingDate: '2023-03-14', balance: 50502734.85 },
  { postingDate: '2023-03-08', balance: 43703685.85 },
  { postingDate: '2023-02-13', balance: 7601834.85 },
  { postingDate: '2023-02-10', balance: 19455807.51 },
  { postingDate: '2023-02-01', balance: 10549844.51 },
]);

const getFullOutstandingBalanceMap = () => {
  let runningDate: Util.FullDateStr = outstandingBalanceData[0].postingDate;
  const endDate: Util.FullDateStr = '2023-08-01';

  const outstandingBalanceMap: { [key: Util.FullDateStr]: number } = {};
  outstandingBalanceData.forEach((entry, idx) => {
    const nextDate = outstandingBalanceData[idx + 1]?.postingDate || endDate;
    // eslint-disable-next-line no-restricted-syntax
    while (runningDate < nextDate) {
      outstandingBalanceMap[runningDate] = entry.balance;
      runningDate = commonDate.addDays(runningDate, 1);
    }
  });

  return outstandingBalanceMap;
};

const getEmptyRow = (period: Util.FullDateStr) =>
  csvHeaders.reduce<{ [key: string]: string | number | undefined }>(
    (row, header) => {
      if (header.key === 'postingDate') return row;
      // Add override for interest rate, margin, and commitment amount
      if (header.key === 'commitmentAmount')
        return { ...row, [header.key]: dollarNumberWithCommas(100000000) };
      if (header.key === 'interestRate')
        return { ...row, [header.key]: sofrTermRates[period] };
      if (header.key === 'interestMargin')
        return { ...row, [header.key]: '10%' };
      return { ...row, [header.key]: '$0.00' };
    },
    {},
  );

// we receive a sorted set of data from the parent component
const formatCsvData = (
  period: Util.FullDateStr,
  ledgerData: LedgerTransactionGQLType[],
): { [key: string]: any }[] => {
  const groupedLedgerData = ledgerData.reduce<{ [key: string]: any }>(
    (summary, entry) => {
      const formattedAmount = dollarNumberWithCommas(entry.amount);
      if (summary[entry.postingDate]) {
        // eslint-disable-next-line no-param-reassign
        summary[entry.postingDate][entry.accountType] = formattedAmount;
      } else {
        // eslint-disable-next-line no-param-reassign
        summary[entry.postingDate] = {
          [entry.accountType]: formattedAmount,
        };
      }
      return summary;
    },
    {},
  );

  // Return fully filled list of data for the period for all expected headers
  const emptyRow = getEmptyRow(period);
  const outstandingBalanceMap = getFullOutstandingBalanceMap();
  const results = [];
  let runningDate = period;
  const startOfNextPeriod = commonDate.addMonths(period, 1);
  // eslint-disable-next-line no-restricted-syntax
  while (runningDate < startOfNextPeriod) {
    const data = groupedLedgerData[runningDate];
    results.push({
      ...emptyRow,
      ...data,
      postingDate: runningDate,
      outstandingBalance: dollarNumberWithCommas(
        outstandingBalanceMap[runningDate],
      ),
    });
    runningDate = commonDate.addDays(runningDate, 1);
  }

  return results;
};

export const MonthlySummaryTableHeader: React.FC<ExpensesFeesTableHeaderProps> = ({
  periods,
  selectedPeriod,
  onChangeHandler,
  isLoading,
  ledgerData,
}) => {
  const filenamePrefix = commonDate
    .fullDateStrToStr(selectedPeriod, 'MMMM yyyy')
    .toLowerCase()
    .split(' ')
    .join('-');
  const dataRows = formatCsvData(selectedPeriod, ledgerData);
  return (
    <HStack spacing="50px" justify="space-between">
      <HStack spacing="50px">
        <Select
          options={periods}
          value={selectedPeriod}
          onChange={(value) => {
            if (value) onChangeHandler(value);
          }}
        />
      </HStack>
      <HStack spacing="10px">
        <Text>Download Daily Audit</Text>
        <CsvDownloadButton
          dataRows={dataRows}
          headers={csvHeaders}
          filenamePrefix={`summary-${filenamePrefix}-downloaded-on`}
          buttonLabel="Export CSV of daily accruals"
          dataIsLoading={isLoading}
        />
      </HStack>
    </HStack>
  );
};
